/**
 * Well Management Table Component
 *
 * Displays all recovery wells and MDPE wells in a table format with:
 * - Toggle to enable/disable individual wells
 * - Well status indicator (pumping/idle)
 * - Key parameters display
 * - Delete functionality
 */

import { useState, useCallback } from 'react';
import { RecoveryWell, MDPEWell } from '../../api/polygon';
import {
  TrashIcon,
  PlayIcon,
  PauseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

// ============================================================================
// TYPES
// ============================================================================

export interface WellManagementTableProps {
  recoveryWells: RecoveryWell[];
  mdpeWells: MDPEWell[];
  onRecoveryWellToggle: (wellId: string, enabled: boolean) => void;
  onMdpeWellToggle: (wellId: string, enabled: boolean) => void;
  onRecoveryWellDelete: (wellId: string) => void;
  onMdpeWellDelete: (wellId: string) => void;
  onRecoveryWellUpdate?: (wellId: string, updates: Partial<RecoveryWell>) => void;
  onMdpeWellUpdate?: (wellId: string, updates: Partial<MDPEWell>) => void;
  currentMonth?: number;
  compact?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatCoordinate(value: number | undefined, decimals: number = 5): string {
  if (value === undefined || value === null) return 'N/A';
  return value.toFixed(decimals);
}

function getWellStatus(well: RecoveryWell | MDPEWell, currentMonth: number = 0): 'active' | 'scheduled' | 'disabled' {
  if (well.enabled === false) return 'disabled';
  if (well.start_month > currentMonth) return 'scheduled';
  return 'active';
}

function getStatusColor(status: 'active' | 'scheduled' | 'disabled'): string {
  switch (status) {
    case 'active': return 'bg-green-500';
    case 'scheduled': return 'bg-yellow-500';
    case 'disabled': return 'bg-mono-600';
  }
}

function getStatusText(status: 'active' | 'scheduled' | 'disabled', startMonth: number): string {
  switch (status) {
    case 'active': return 'Pumping';
    case 'scheduled': return `Starts M${startMonth}`;
    case 'disabled': return 'Off';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function WellManagementTable({
  recoveryWells,
  mdpeWells,
  onRecoveryWellToggle,
  onMdpeWellToggle,
  onRecoveryWellDelete,
  onMdpeWellDelete,
  onRecoveryWellUpdate,
  onMdpeWellUpdate,
  currentMonth = 0,
  compact = false,
}: WellManagementTableProps) {
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<'recovery' | 'mdpe'>('recovery');

  // Ensure arrays are never undefined
  const safeRecoveryWells = recoveryWells || [];
  const safeMdpeWells = mdpeWells || [];

  const totalWells = safeRecoveryWells.length + safeMdpeWells.length;
  const activeRecoveryWells = safeRecoveryWells.filter(w => w.enabled !== false && w.start_month <= currentMonth);
  const activeMdpeWells = safeMdpeWells.filter(w => w.enabled !== false && w.start_month <= currentMonth);

  const totalRecoveryGPD = activeRecoveryWells.reduce((sum, w) => sum + w.pump_rate_gpd, 0);
  const totalMdpeCFM = activeMdpeWells.reduce((sum, w) => sum + w.extraction_cfm, 0);

  // Memoized handlers for well actions to prevent recreating functions on every render
  const handleRecoveryToggle = useCallback((wellId: string, shouldEnable: boolean) => {
    onRecoveryWellToggle(wellId, shouldEnable);
  }, [onRecoveryWellToggle]);

  const handleRecoveryDelete = useCallback((wellId: string) => {
    onRecoveryWellDelete(wellId);
  }, [onRecoveryWellDelete]);

  const handleMdpeToggle = useCallback((wellId: string, shouldEnable: boolean) => {
    onMdpeWellToggle(wellId, shouldEnable);
  }, [onMdpeWellToggle]);

  const handleMdpeDelete = useCallback((wellId: string) => {
    onMdpeWellDelete(wellId);
  }, [onMdpeWellDelete]);

  if (totalWells === 0) {
    return null;
  }

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden">
      {/* Header - always visible */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-mono-850 cursor-pointer hover:bg-mono-800 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
            Well Management
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded bg-mono-700 text-mono-300">
              {totalWells} well{totalWells !== 1 ? 's' : ''}
            </span>
            {activeRecoveryWells.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded bg-cyan-900/50 text-cyan-400">
                {totalRecoveryGPD.toLocaleString()} GPD
              </span>
            )}
            {activeMdpeWells.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded bg-amber-900/50 text-amber-400">
                {totalMdpeCFM.toLocaleString()} CFM
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-[10px] text-mono-500">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span>{activeRecoveryWells.length + activeMdpeWells.length} active</span>
          </div>
          {expanded ? (
            <ChevronUpIcon className="w-4 h-4 text-mono-500" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-mono-500" />
          )}
        </div>
      </div>

      {/* Expandable content */}
      {expanded && (
        <div className="p-3">
          {/* Tabs */}
          <div className="flex border-b border-mono-800 mb-3">
            <button
              onClick={() => setActiveTab('recovery')}
              className={`px-4 py-2 text-xs font-medium transition-colors relative ${
                activeTab === 'recovery'
                  ? 'text-cyan-400'
                  : 'text-mono-500 hover:text-mono-300'
              }`}
            >
              Recovery Wells ({safeRecoveryWells.length})
              {activeTab === 'recovery' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('mdpe')}
              className={`px-4 py-2 text-xs font-medium transition-colors relative ${
                activeTab === 'mdpe'
                  ? 'text-amber-400'
                  : 'text-mono-500 hover:text-mono-300'
              }`}
            >
              MDPE Wells ({safeMdpeWells.length})
              {activeTab === 'mdpe' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400"></div>
              )}
            </button>
          </div>

          {/* Recovery Wells Table */}
          {activeTab === 'recovery' && (
            <div className="overflow-x-auto">
              {safeRecoveryWells.length === 0 ? (
                <p className="text-xs text-mono-500 text-center py-4">
                  No recovery wells placed. Click on the map to add wells.
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-mono-500 uppercase text-[10px]">
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Well</th>
                      <th className="text-right py-2 px-2">Rate (GPD)</th>
                      <th className="text-right py-2 px-2">Start</th>
                      <th className="text-right py-2 px-2">Position</th>
                      <th className="text-center py-2 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeRecoveryWells.map((well, idx) => {
                      const status = getWellStatus(well, currentMonth);
                      const wellName = well.name || `RW-${idx + 1}`;
                      return (
                        <tr
                          key={well.id || idx}
                          className={`border-t border-mono-800 ${
                            status === 'disabled' ? 'opacity-50' : ''
                          }`}
                        >
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getStatusColor(status)}`}></div>
                              <span className={`${
                                status === 'active' ? 'text-green-400' :
                                status === 'scheduled' ? 'text-yellow-400' : 'text-mono-500'
                              }`}>
                                {getStatusText(status, well.start_month)}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <span className="text-cyan-400 font-medium">{wellName}</span>
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-mono-300">
                            {well.pump_rate_gpd.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right text-mono-400">
                            M{well.start_month}
                          </td>
                          <td className="py-2 px-2 text-right text-mono-500 text-[10px]">
                            {well.latitude && well.longitude ? (
                              <span title={`${well.latitude}, ${well.longitude}`}>
                                {formatCoordinate(well.latitude, 4)}, {formatCoordinate(well.longitude, 4)}
                              </span>
                            ) : (
                              <span>{well.x_ft.toFixed(0)}ft, {well.y_ft.toFixed(0)}ft</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleRecoveryToggle(well.id || String(idx), well.enabled === false)}
                                className={`p-1 rounded transition-colors ${
                                  well.enabled === false
                                    ? 'text-mono-500 hover:text-green-400 hover:bg-green-900/20'
                                    : 'text-green-400 hover:text-yellow-400 hover:bg-yellow-900/20'
                                }`}
                                title={well.enabled === false ? 'Turn on' : 'Turn off'}
                              >
                                {well.enabled === false ? (
                                  <PlayIcon className="w-4 h-4" />
                                ) : (
                                  <PauseIcon className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleRecoveryDelete(well.id || String(idx))}
                                className="p-1 rounded text-mono-500 hover:text-red-400 hover:bg-red-900/20 transition-colors"
                                title="Delete well"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-mono-700 bg-mono-850/50">
                      <td colSpan={2} className="py-2 px-2 text-mono-400">
                        Total Active
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-cyan-400 font-medium">
                        {totalRecoveryGPD.toLocaleString()}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}

          {/* MDPE Wells Table */}
          {activeTab === 'mdpe' && (
            <div className="overflow-x-auto">
              {safeMdpeWells.length === 0 ? (
                <p className="text-xs text-mono-500 text-center py-4">
                  No MDPE wells placed. Click on the map to add wells.
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-mono-500 uppercase text-[10px]">
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Well</th>
                      <th className="text-right py-2 px-2">Rate (CFM)</th>
                      <th className="text-right py-2 px-2">Start</th>
                      <th className="text-right py-2 px-2">Position</th>
                      <th className="text-center py-2 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeMdpeWells.map((well, idx) => {
                      const status = getWellStatus(well, currentMonth);
                      const wellName = well.name || `MDPE-${idx + 1}`;
                      return (
                        <tr
                          key={well.id || idx}
                          className={`border-t border-mono-800 ${
                            status === 'disabled' ? 'opacity-50' : ''
                          }`}
                        >
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getStatusColor(status)}`}></div>
                              <span className={`${
                                status === 'active' ? 'text-green-400' :
                                status === 'scheduled' ? 'text-yellow-400' : 'text-mono-500'
                              }`}>
                                {getStatusText(status, well.start_month)}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <span className="text-amber-400 font-medium">{wellName}</span>
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-mono-300">
                            {well.extraction_cfm.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right text-mono-400">
                            M{well.start_month}
                          </td>
                          <td className="py-2 px-2 text-right text-mono-500 text-[10px]">
                            {well.latitude && well.longitude ? (
                              <span title={`${well.latitude}, ${well.longitude}`}>
                                {formatCoordinate(well.latitude, 4)}, {formatCoordinate(well.longitude, 4)}
                              </span>
                            ) : (
                              <span>{well.x_ft.toFixed(0)}ft, {well.y_ft.toFixed(0)}ft</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleMdpeToggle(well.id || String(idx), well.enabled === false)}
                                className={`p-1 rounded transition-colors ${
                                  well.enabled === false
                                    ? 'text-mono-500 hover:text-green-400 hover:bg-green-900/20'
                                    : 'text-green-400 hover:text-yellow-400 hover:bg-yellow-900/20'
                                }`}
                                title={well.enabled === false ? 'Turn on' : 'Turn off'}
                              >
                                {well.enabled === false ? (
                                  <PlayIcon className="w-4 h-4" />
                                ) : (
                                  <PauseIcon className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleMdpeDelete(well.id || String(idx))}
                                className="p-1 rounded text-mono-500 hover:text-red-400 hover:bg-red-900/20 transition-colors"
                                title="Delete well"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-mono-700 bg-mono-850/50">
                      <td colSpan={2} className="py-2 px-2 text-mono-400">
                        Total Active
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-amber-400 font-medium">
                        {totalMdpeCFM.toLocaleString()}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}

          {/* Quick actions */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-mono-800">
            <div className="flex items-center gap-2">
              {activeTab === 'recovery' && safeRecoveryWells.length > 0 && (
                <>
                  <button
                    onClick={() => safeRecoveryWells.forEach(w => w.id && handleRecoveryToggle(w.id, true))}
                    className="text-[10px] px-2 py-1 rounded bg-green-900/30 text-green-400 hover:bg-green-900/50 transition-colors"
                  >
                    Enable All
                  </button>
                  <button
                    onClick={() => safeRecoveryWells.forEach(w => w.id && handleRecoveryToggle(w.id, false))}
                    className="text-[10px] px-2 py-1 rounded bg-mono-800 text-mono-400 hover:bg-mono-700 transition-colors"
                  >
                    Disable All
                  </button>
                </>
              )}
              {activeTab === 'mdpe' && safeMdpeWells.length > 0 && (
                <>
                  <button
                    onClick={() => safeMdpeWells.forEach(w => w.id && handleMdpeToggle(w.id, true))}
                    className="text-[10px] px-2 py-1 rounded bg-green-900/30 text-green-400 hover:bg-green-900/50 transition-colors"
                  >
                    Enable All
                  </button>
                  <button
                    onClick={() => safeMdpeWells.forEach(w => w.id && handleMdpeToggle(w.id, false))}
                    className="text-[10px] px-2 py-1 rounded bg-mono-800 text-mono-400 hover:bg-mono-700 transition-colors"
                  >
                    Disable All
                  </button>
                </>
              )}
            </div>
            <div className="text-[10px] text-mono-600">
              Toggle wells to compare pumping scenarios
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WellManagementTable;
